import { BUILDING_EXTERIOR_LAYER, BUILDING_INTERIOR_LAYER, CLIPPING_LAYER } from "gfx/layerConstants";
import { MeshHardcodedMaterialType } from "gfx/MaterialFactory";
import type RenderContext from "lib/house/render/RenderContext";
import * as THREE from "three";

export async function loadDeselectedOverrideMaterial(ctx: RenderContext): Promise<THREE.Material | undefined> {
  return ctx.materialFactory.getHardcodedMaterial(MeshHardcodedMaterialType.DESELECTED_OVERRIDE);
}

enum DeselectedReportingUnitLayer {
  KEEP_PREVIOUS_LAYERS,
  EXTERIOR,
  INTERIOR,
}

// Assigning all deselected reporting unit geometry to the exterior layer fixes non-uniform lighting around windows and doors.
const DESELECTED_REPORTING_UNIT_LAYER: DeselectedReportingUnitLayer = DeselectedReportingUnitLayer.EXTERIOR;

export function setMeshLayersForDeselectedReportingUnit(mesh: THREE.Object3D) {
  const isClipping = mesh.layers.isEnabled(CLIPPING_LAYER);
  if (
    DESELECTED_REPORTING_UNIT_LAYER === DeselectedReportingUnitLayer.EXTERIOR &&
    mesh.layers.isEnabled(BUILDING_INTERIOR_LAYER)
  ) {
    mesh.layers.disableAll();
    mesh.layers.enable(BUILDING_EXTERIOR_LAYER);
    if (isClipping) {
      mesh.layers.enable(CLIPPING_LAYER);
    }
  }
  if (
    DESELECTED_REPORTING_UNIT_LAYER === DeselectedReportingUnitLayer.INTERIOR &&
    mesh.layers.isEnabled(BUILDING_EXTERIOR_LAYER)
  ) {
    mesh.layers.disableAll();
    mesh.layers.enable(BUILDING_INTERIOR_LAYER);
    if (isClipping) {
      mesh.layers.enable(CLIPPING_LAYER);
    }
  }
}

export function isOutsideSelectedReportingUnit(
  reportingUnitLabelId: string | undefined,
  selectedReportingUnitLabelId: string | undefined,
): boolean {
  if (selectedReportingUnitLabelId) {
    return reportingUnitLabelId !== selectedReportingUnitLabelId;
  }
  // If there's no selected reporting unit, then we don't apply the reporting unit override material to anything.
  return false;
}

/** Returns true if mesh is using reporting unit override material */
export async function assignMeshReportingUnitOverrideMaterial(
  mesh: THREE.Object3D,
  ctx: RenderContext,
  reportingUnitLabelId: string | undefined,
): Promise<boolean> {
  if (!ctx.overrideMaterialOnDeselectedReportingUnits) {
    return false;
  }
  if (!isOutsideSelectedReportingUnit(reportingUnitLabelId, ctx.selectedReportingUnitLabelId)) {
    return false;
  }

  const deselectedOverrideMaterial = await loadDeselectedOverrideMaterial(ctx);
  if (!deselectedOverrideMaterial) {
    return false;
  }

  mesh.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      obj.material = deselectedOverrideMaterial;
    }
  });
  setMeshLayersForDeselectedReportingUnit(mesh);
  return true;
}
